import { sourceDetail, sourceLabel } from '@app/model/AutomationMessage';
import { AutomationMessageSource, AutomationMessageType } from '@app/model/Enums';
import { Flex, FlexItem, Icon, Tooltip } from '@patternfly/react-core';
import {
  ExclamationCircleIcon, ExclamationTriangleIcon, InfoCircleIcon, ServerIcon, TableIcon
} from '@patternfly/react-icons';
import * as React from 'react';

/**
 * How an automation message's severity and its source are drawn, in one place because two screens
 * now show the same messages: the Automation Messages panel, where they are worked through, and a
 * class's own title bar, where the class being reported on is what a reader arrived for. A message
 * that reads as an error on one and a warning on the other would be read as two findings.
 */

interface AutomationMessageTypeIconProps {
  type: AutomationMessageType;
  /**
   * An accessible name for the icon. Pass it where the icon alone carries the severity; leave it
   * off where the type's name is written beside it, so a screen reader does not hear it twice.
   */
  label?: string;
}

/**
 * The severity icon for a message's type.
 *
 * A type this build cannot name gets the neutral info glyph without a status colour: the value is
 * the service's, and colouring an unrecognised one red or amber would be a claim about how loud it
 * is that nothing supports.
 */
export const AutomationMessageTypeIcon: React.FC<AutomationMessageTypeIconProps> = ({ type, label }) => {
  const named = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  switch (type) {
    case AutomationMessageType.ERROR:
      return <Icon status="danger" {...named}><ExclamationCircleIcon /></Icon>;
    case AutomationMessageType.WARNING:
      return <Icon status="warning" {...named}><ExclamationTriangleIcon /></Icon>;
    case AutomationMessageType.INFO:
      return <Icon status="info" {...named}><InfoCircleIcon /></Icon>;
    default:
      return <Icon {...named}><InfoCircleIcon /></Icon>;
  }
};

/**
 * The sheet the import read, or the service that could not place what it read. The icon carries the
 * distinction alongside the words, so a reader scrolling past knows which of the two they are
 * looking at without reading it.
 *
 * A source this build cannot name gets the neutral info icon rather than either of these: guessing
 * which of the two systems it resembles would be a claim about it that nothing supports.
 */
export const AutomationMessageSourceIcon: React.FC<{ source: string }> = ({ source }) => {
  switch (source) {
    case AutomationMessageSource.AUTOMATION:
      return <Icon><TableIcon /></Icon>;
    case AutomationMessageSource.CAS:
      return <Icon><ServerIcon /></Icon>;
    default:
      return <Icon status="info"><InfoCircleIcon /></Icon>;
  }
};

/**
 * The source's icon and name together, with what it means for whoever has to clear the message on
 * hover - the one line that says whose hands fix it, which is the question a reader has once they
 * have read the message itself.
 */
export const AutomationMessageSourceText: React.FC<{ source: string }> = ({ source }) => (
  <Tooltip content={sourceDetail(source)}>
    <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}
      flexWrap={{ default: 'nowrap' }} display={{ default: 'inlineFlex' }}>
      <FlexItem><AutomationMessageSourceIcon source={source} /></FlexItem>
      <FlexItem>{sourceLabel(source)}</FlexItem>
    </Flex>
  </Tooltip>
);
